import { HEN_BREEDS } from '../data/breeds';
import { ROOSTER_BREEDS } from '../data/roosters';
import type { BreedId } from '../data/types';

/**
 * Sprite sheets carried over from v0.9 (procedurally generated pixel art):
 *  - farm-sprites.png:    hens, 32×32 frames. 3 variant blocks × 8 frames per
 *                         row; row = breed.spriteRow.
 *  - rooster-sprites.png: roosters, 64×64 frames, same block layout.
 * Frames: 0–1 idle/blink, 2–3 peck, 6 alert, 7 fluffed (click reaction).
 */

const base = import.meta.env.BASE_URL;

function load(src: string): Promise<HTMLImageElement | null> {
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => resolve(null);
    img.src = src;
  });
}

let henSheet: HTMLImageElement | null = null;
let roosterSheet: HTMLImageElement | null = null;
const backgrounds = new Map<string, HTMLImageElement | null>();

export async function loadSprites(): Promise<void> {
  [henSheet, roosterSheet] = await Promise.all([
    load(`${base}assets/sprites/farm-sprites.png`),
    load(`${base}assets/sprites/rooster-sprites.png`),
  ]);
}

export function background(key: string): HTMLImageElement | null {
  if (!backgrounds.has(key)) {
    backgrounds.set(key, null);
    void load(`${base}assets/backgrounds/${key}.png`).then((img) => backgrounds.set(key, img));
  }
  return backgrounds.get(key) ?? null;
}

const FALLBACK: Partial<Record<BreedId, string>> = { RIR: '#8b2500', LEGHORN: '#fffff0', BUFF_ORPINGTON: '#daa520', WYANDOTTE: '#c0c0c0' };

export function drawHen(ctx: CanvasRenderingContext2D, breed: BreedId, variant: number, frame: number, x: number, y: number, size: number): void {
  const row = HEN_BREEDS[breed].spriteRow;
  if (henSheet) {
    ctx.imageSmoothingEnabled = false;
    ctx.drawImage(henSheet, Math.max(0, Math.min(2, variant)) * 256 + frame * 32, row * 32, 32, 32, x, y, size, size);
    return;
  }
  ctx.fillStyle = FALLBACK[breed] ?? '#a0522d';
  ctx.beginPath();
  ctx.ellipse(x + size / 2, y + size * 0.55, size * 0.35, size * 0.3, 0, 0, Math.PI * 2);
  ctx.fill();
}

export function drawRoosterSprite(ctx: CanvasRenderingContext2D, breed: BreedId, variant: number, frame: number, x: number, y: number, size: number): void {
  const row = ROOSTER_BREEDS[breed].spriteRow;
  if (roosterSheet) {
    ctx.imageSmoothingEnabled = false;
    ctx.drawImage(roosterSheet, Math.max(0, Math.min(2, variant)) * 512 + frame * 64, row * 64, 64, 64, x, y, size, size);
    return;
  }
  ctx.fillStyle = '#c0392b';
  ctx.fillRect(x + size * 0.2, y + size * 0.2, size * 0.6, size * 0.6);
}
